import React from 'react';
import { intentAccounts } from '../../mock';

// 5 intent sources columns: P (Predictor) B (Bombora) Z (ZoomInfo) LI (LinkedIn) EV (EvolveBPM 1st-party)
const SOURCES = [
  { key: 'P', label: 'Predictor', color: '#4ECDC4' },
  { key: 'B', label: 'Bombora', color: '#4285F4' },
  { key: 'Z', label: 'ZoomInfo', color: '#FBBC05' },
  { key: 'LI', label: 'LinkedIn', color: '#9333EA' },
  { key: 'EV', label: '1st-Party', color: '#34A853' },
];

// Deterministic per-source score derived from combined score
function sourceScores(overall) {
  // vary by ±18% around overall to feel realistic
  const variance = [-4, 7, -9, 3, 5];
  return variance.map((v) => Math.max(0, Math.min(100, Math.round(overall + v))));
}

function heatBg(v) {
  // 0..100 mapped to a heat scale (soft → vivid teal/orange)
  if (v >= 80) return { bg: '#4ECDC4', text: '#0F1C40' };
  if (v >= 65) return { bg: 'rgba(78,205,196,0.55)', text: '#0F1C40' };
  if (v >= 50) return { bg: 'rgba(78,205,196,0.28)', text: '#F1EDE4' };
  if (v >= 35) return { bg: 'rgba(78,205,196,0.14)', text: '#F1EDE4' };
  return { bg: 'rgba(255,255,255,0.05)', text: '#F1EDE4' };
}

export default function IntentScoring() {
  return (
    <section className="py-20 lg:py-32 bg-[#0F1C40] text-[#F1EDE4]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-12 lg:mb-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
              <span className="w-6 h-px bg-[#4ECDC4]" />
              <span>ABM Playbook</span>
            </div>
            <h2 className="text-4xl lg:text-6xl tracking-tight leading-[1.02] font-medium">
              Multi-source <span className="font-serif italic font-normal text-[#4ECDC4]">intent scoring matrix.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 self-end">
            <p className="text-white/70 text-[15px] max-w-lg">
              From 5 intent sources → 1 consolidated score → priority ABM targets across 281 ANZ accounts.
            </p>
          </div>
        </div>

        {/* Source legend */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {SOURCES.map((s) => (
            <div key={s.key} className="flex items-center gap-3 bg-[#12234C] border border-white/10 rounded-xl px-4 py-3">
              <span className="w-3 h-3 rounded-full" style={{ background: s.color }} />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Src {s.key}</div>
                <div className="text-sm font-medium leading-tight">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Matrix */}
        <div className="bg-[#12234C] border border-white/10 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-5 py-4 bg-white/5 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50">
            <div className="col-span-1">#</div>
            <div className="col-span-4">Account</div>
            {SOURCES.map((s) => (
              <div key={s.key} className="col-span-1 text-center">{s.key}</div>
            ))}
            <div className="col-span-2 text-right">Combined</div>
          </div>
          {intentAccounts.map((a, i) => {
            const scores = sourceScores(a.score);
            return (
              <div
                key={a.rank}
                className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/5 items-center hover:bg-white/[0.03] transition-colors"
              >
                <div className="col-span-1 font-mono text-sm text-white/50">{a.rank}</div>
                <div className="col-span-4 text-sm font-medium">{a.name}</div>
                {scores.map((v, j) => {
                  const st = heatBg(v);
                  return (
                    <div key={j} className="col-span-1 h-9 rounded-md flex items-center justify-center font-mono text-xs font-semibold tabular" style={{ background: st.bg, color: st.text }}>
                      {v}
                    </div>
                  );
                })}
                <div className="col-span-2 text-right flex items-center justify-end gap-2">
                  <div className="flex-1 h-1 bg-white/10 rounded-full max-w-[80px]">
                    <div className="h-full bg-[#4ECDC4] rounded-full" style={{ width: `${a.score}%` }} />
                  </div>
                  <span className={`font-mono text-sm font-semibold tabular ${a.score > 80 ? 'text-[#4ECDC4]' : a.score > 65 ? 'text-[#F1EDE4]' : 'text-white/60'}`}>{a.score.toFixed(1)}</span>
                </div>
              </div>
            );
          })}
          <div className="px-5 py-3 bg-white/5 flex items-center gap-3 text-[11px] font-mono">
            <span className="px-2 py-0.5 bg-[#4ECDC4] text-[#0F1C40] rounded-sm">NEW</span>
            <span className="text-white/70">Dark-account lift (1st-party) surfaced 14 unresearched accounts this quarter</span>
          </div>
        </div>

        {/* Heat legend */}
        <div className="mt-6 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-white/50">
          <div className="flex items-center gap-2">
            <span>Low</span>
            {['rgba(255,255,255,0.05)', 'rgba(78,205,196,0.14)', 'rgba(78,205,196,0.28)', 'rgba(78,205,196,0.55)', '#4ECDC4'].map((c) => (
              <span key={c} className="w-6 h-3 rounded-sm" style={{ background: c }} />
            ))}
            <span>High</span>
          </div>
          <div>281 ANZ accounts · refreshed continuously</div>
        </div>
      </div>
    </section>
  );
}
